import { fn, hash } from '@ember/helper';
import { service } from '@ember/service';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';

import Toolbar from 'umg-ember-backend/components/record-detail/toolbar';
import Window from 'umg-ember-backend/components/ui/window';
import recordHeadlineOrNew from 'umg-ember-backend/helpers/record-headline-or-new';
import recordOverline from 'umg-ember-backend/helpers/record-overline';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class RecordDetailForDefaultComponent extends Component {
  // Services
  @service breadcrumb;
  @service flashMessages;
  @service history;
  @service intl;
  @service recordLocks;
  @service recordRelationships;
  @service router;
  @service store;

  // Defaults
  @tracked model;
  @storeOnRoute('routeStorageKey') scrollPosition;

  // Constructor
  constructor() {
    super(...arguments);

    this.history.showErrors = false;
  }

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.model).lid;
  }

  get RecordDetailForm() {
    let module = importSync(`../../forms/${getRecordType(this.args.model)}`);
    return module.default;
  }

  // Actions
  transitionToPrevBreadcrumb = () => {
    if (this.breadcrumb.prevRoute) {
      this.router.transitionTo(...this.breadcrumb.prevRoute.transitionToParams);
    }
  };

  deleteRecordInDetail = async (model) => {
    let ok = await this.history.confirmAction('delete', model);

    if (!ok) {
      return;
    }

    try {
      await this.history.deleteModel(model);

      this.transitionToPrevBreadcrumb();
    } catch (error) {
      await model.rollbackAttributes();
      await this.recordRelationships.rollback(model);

      this.flashMessages.serverError(error);
    }
  };

  forceUnlockRecord = (model) => {
    return this.recordLocks.forceUnlock(model);
  };

  publishRecord = async (model) => {
    let ok = await this.history.confirmAction('publish', model);

    if (!ok) {
      return;
    }

    try {
      this.history.publishModel(model);
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  saveAndUnloadRecord = async (model) => {
    let savedModel = await this.saveModel(model);

    if (savedModel) {
      this.transitionToPrevBreadcrumb();
    }
  };

  saveRecord = (model) => {
    this.saveModel(model);
  };

  unblockRecord = (model) => {
    model.cmsState = 1;

    this.saveModel(model);
  };

  blockRecord = (model) => {
    model.cmsState = 6;

    this.saveModel(model);
  };

  saveModel = async (model) => {
    try {
      return await this.history.saveModel(model);
    } catch {
      return;
    }
  };

  // Template
  <template>
    <Window
      @onBodyScroll={{fn (mut this.scrollPosition)}}
      @scrollPosition={{this.scrollPosition}}
      @isLoading={{@model.isLoading}}
      data-test-record-detail='default'
    >
      <:overline>
        {{recordOverline @model}}
      </:overline>

      <:title>
        {{recordHeadlineOrNew @model}}
      </:title>

      <:toolbar>
        <Toolbar
          @model={{@model}}
          @primaryActions={{hash
            onCancel=this.transitionToPrevBreadcrumb
            onPublish=(fn this.publishRecord @model)
            onSave=(fn this.saveRecord @model)
            onSaveAndUnload=(fn this.saveAndUnloadRecord @model)
          }}
          @secondaryActions={{hash
            onDeleteRecord=(fn this.deleteRecordInDetail @model)
            onForceUnlock=(fn this.forceUnlockRecord @model)
            onUnblock=(fn this.unblockRecord @model)
            onBlock=(fn this.blockRecord @model)
          }}
        />
      </:toolbar>

      <:body>
        <this.RecordDetailForm
          @model={{@model}}
          @recordType={{getRecordType @model}}
          @onSaveRecord={{fn this.saveRecord @model}}
          @showErrors={{this.history.showErrors}}
        />
      </:body>
    </Window>
  </template>
}
